import * as React from 'react'
import i18next from 'i18next'
import { ArrowsUpDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { TwButton } from '../Button/TwButton'
import { Tooltip } from '../Tooltip/Tooltip.component'
import { AnchoredMenu } from '../AnchoredMenu'
import { GallerySort as GallerySortValue, isDefaultSort } from './gallerySortParams'
import type { OrderType, ResourcesOrdering } from '../../services/api'

interface GallerySortProps {
  sort: GallerySortValue
  onChange: (next: GallerySortValue) => void
  /** date_added_to_album only sorts server-side when an album filter is set */
  allowAddedToAlbum?: boolean
}

const RadioRow: React.FC<{
  selected: boolean
  onClick: () => void
  label: string
}> = ({ selected, onClick, label }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    className={`flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors ${
      selected ? 'font-medium text-indigo-700' : 'text-gray-700 hover:bg-gray-50'
    }`}
  >
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
        selected ? 'border-indigo-600' : 'border-gray-300'
      }`}
    >
      {selected && <span className="h-2 w-2 rounded-full bg-indigo-600" />}
    </span>
    {label}
  </button>
)

const orderByLabel = (orderBy: ResourcesOrdering): string => {
  switch (orderBy) {
    case 'date_uploaded':
      return i18next.t('gallerySort.dateUploaded')
    case 'date_added_to_album':
      return i18next.t('gallerySort.dateAddedToAlbum')
    default:
      return i18next.t('gallerySort.dateTaken')
  }
}

export const GallerySort: React.FC<GallerySortProps> = ({
  sort,
  onChange,
  allowAddedToAlbum = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)

  const orderByOptions: Array<ResourcesOrdering> = [
    'date_taken',
    'date_uploaded',
    ...(allowAddedToAlbum ? (['date_added_to_album'] as Array<ResourcesOrdering>) : []),
  ]
  const orderOptions: Array<OrderType> = ['desc', 'asc']

  return (
    <div className="relative">
      <Tooltip label={i18next.t('gallerySort.title')} align="right">
        <TwButton
          variant={isDefaultSort(sort) ? 'outline' : 'solid'}
          icon={<ArrowsUpDownIcon />}
          onClick={() => setIsOpen(true)}
          aria-label={i18next.t('gallerySort.title')}
        >
          <span className="hidden md:inline">{orderByLabel(sort.orderBy)}</span>
          <ChevronDownIcon className="hidden h-4 w-4 md:inline" />
        </TwButton>
      </Tooltip>

      <AnchoredMenu
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={i18next.t('gallerySort.title')}
      >
        <div role="radiogroup" aria-label={i18next.t('gallerySort.by')}>
          <p className="mb-1 text-sm font-medium text-gray-700">{i18next.t('gallerySort.by')}</p>
          {orderByOptions.map((option) => (
            <RadioRow
              key={option}
              selected={sort.orderBy === option}
              onClick={() => onChange({ ...sort, orderBy: option })}
              label={orderByLabel(option)}
            />
          ))}
        </div>

        <div role="radiogroup" aria-label={i18next.t('gallerySort.order')}>
          <p className="mb-1 text-sm font-medium text-gray-700">{i18next.t('gallerySort.order')}</p>
          {orderOptions.map((option) => (
            <RadioRow
              key={option}
              selected={sort.order === option}
              onClick={() => onChange({ ...sort, order: option })}
              label={i18next.t(option === 'asc' ? 'gallerySort.asc' : 'gallerySort.desc')}
            />
          ))}
        </div>
      </AnchoredMenu>
    </div>
  )
}
